import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Input from "../../../components/Input/Input"
import Button from "../../../components/Button/Button"
import TextArea from "../../../components/TextArea/TextArea"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import Breadcrumbs from "../../../components/BreadCrumbs/Breadcrumbs"
import {
  createNewContactGroup,
  getContactGroupById,
  updateContactGroup,
  getContactGroupAffiliates,
  fetchLsaSetOptions
} from "../../../features/contactGroups/contactGroups"
import { fetchAllAffiliatesDropdown } from "../../../features/affiliates/affiliates"
import Dropdown from "../../../components/Dropdown/Dropdown"
import { useNavigate, useParams } from "react-router-dom"
import ContainerPage from "../../../components/ContainerPage/ContainerPage"
import { CrossCloseIcon } from "../../../components/Icons/Icons"
import Switch from "../../../components/Switch/Switch"
import OfficeEmailMuteHint from "../../../components/EmailSettings/OfficeEmailMuteHint"
import { Collapse } from "@mui/material"
import { LuHelpCircle } from "react-icons/lu"
import { isFlatFeeAllowed, isFlatFeeOnlyOffice, hidesLsaPaymentSchedule, FLAT_FEE_ONLY_FLAG } from "../../../constants/feeType"
import { isAffiliateBilledOffice, affiliateCollectsCards, AFFILIATE_COLLECTS_CARDS_FLAG } from "../../../constants/affiliateBilled"
import { REVERSAL_FLAG, isReversalOffice } from "../../../constants/reversalStatuses"
import { DOCUMENTS_AUDIO_FLAG, isCallRecordingsOffice } from "../../../constants/callRecordings"
import { PAYMENT_METHOD_OPTIONS, optionValueOf } from "../../../constants/paymentMethods"
import { PAYMENTS_OFFICE_METHODS, OFFICE_LEAD_SOURCES, ESIGN_LSA_PAYMENT_UPFRONT } from "../../../config/featureFlagKeys"
import { LSA_SET_CHOICES, LSA_SET_STANDARD } from "../../../constants/lsaSets"
import { selectFeatureFlag } from "../../../features/featureFlags/selectors"

let tierUidCounter = 0
const nextUid = () => {
  tierUidCounter += 1
  return `tier-${Date.now()}-${tierUidCounter}`
}

const makeEmptyTier = (isFallback = false) => ({
  _uid: nextUid(),
  maxAmount: isFallback ? null : '',
  programFee: null,
  custodialFee: 50,
  programLength: null,
})

const initialTiers = () => [makeEmptyTier(true)]

const HintIcon = ({ children }) => (
  <div className="relative group inline-block">
    <LuHelpCircle size={16} className="text-text-secondary cursor-help hover:text-text-primary transition-colors" />
    <div className="absolute z-50 w-72 p-3 text-sm bg-paper border border-divider rounded-lg shadow-xl opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-200 right-0 top-6">
      {children}
    </div>
  </div>
)

const AFFILIATE_COLLECTS_CARDS_ON_TEXT = "The affiliate charges clients' cards itself, so we never charge a client's card here. Each card payment waits for someone to mark it paid."
const AFFILIATE_COLLECTS_CARDS_OFF_TEXT = "We charge clients' cards here through the firm's processor, the same as bank accounts. Clients billed to the affiliate are still never charged."
const AFFILIATE_BILLED_ON_TEXT = "Clients here may enrol without a bank account or card. A bank account on file is charged. A card on file is not charged. Applies to all clients of this office right away."
const AFFILIATE_BILLED_ON_WITH_CARDS_SETTING_TEXT = "Clients here may enrol without a bank account or card. A bank account on file is charged. A card on file is charged only when 'The affiliate also collects card payments' is off. Applies to all clients of this office right away."

const ContactGroupControl = ({ handleBreadcrumbClick, isEdit }) => {
  const BreadcrumbsTitleActive = ["Settings"]
  const BreadcrumbsTitle = ["Contact Groups", isEdit ? "Edit Contact Group" : "Add Contact Group"]

  const dispatch = useDispatch()
  const reversalStatusesEnabled = useSelector(selectFeatureFlag(REVERSAL_FLAG))
  const callRecordingsEnabled = useSelector(selectFeatureFlag(DOCUMENTS_AUDIO_FLAG))
  const paymentMethodsEnabled = useSelector(selectFeatureFlag(PAYMENTS_OFFICE_METHODS))
  const flatFeeOnlyEnabled = useSelector(selectFeatureFlag(FLAT_FEE_ONLY_FLAG))
  const officeLeadSourcesEnabled = useSelector(selectFeatureFlag(OFFICE_LEAD_SOURCES))
  const lsaSetEnabled = useSelector(selectFeatureFlag(ESIGN_LSA_PAYMENT_UPFRONT))
  const affiliateCollectsCardsEnabled = useSelector(selectFeatureFlag(AFFILIATE_COLLECTS_CARDS_FLAG))
  const navigate = useNavigate()
  const { groupId } = useParams()
  const { triggerLoading } = useMyContextLoading()
  const [groupInfo, setGroupInfo] = useState({
    name: '',
    notificationEmail: '',
    isDepartment: false,
    qcCallEnabled: false,
    qcCallDaysBeforeWelcome: 3,
    customSettings: {
      programPlanDefaults: {
        enabled: false,
        tiers: initialTiers(),
      },
      disableInitialFee: true,
      lsaTemplateType: 'english',
      dashboardVisibility: 'FULL',
    },
  })
  const [lsaSetOptions, setLsaSetOptions] = useState([])
  const [description, setDescription] = useState('')
  const [reqError, setReqError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [owners, setOwners] = useState([])
  const [supervisors, setSupervisors] = useState([])
  const [groupAffiliates, setGroupAffiliates] = useState([])
  const [legacyUsersIndex, setLegacyUsersIndex] = useState([])
  const [defaultsOverflowVisible, setDefaultsOverflowVisible] = useState(false)

  const defaultsToggleEnabled = !!groupInfo?.customSettings?.programPlanDefaults?.enabled
  const acceptedMethodsValue = optionValueOf(groupInfo?.customSettings?.acceptedPaymentMethods)
  const currentLsaSet = groupInfo?.customSettings?.lsaSet || LSA_SET_STANDARD
  const offeredLsaSets = LSA_SET_CHOICES.filter((choice) => lsaSetOptions.includes(choice.value))
  const affiliateBilledOnText = affiliateCollectsCardsEnabled ? AFFILIATE_BILLED_ON_WITH_CARDS_SETTING_TEXT : AFFILIATE_BILLED_ON_TEXT

  useEffect(() => {
    if (!lsaSetEnabled) return
    const loadLsaSetOptions = async () => {
      const response = await dispatch(fetchLsaSetOptions())
      if (Array.isArray(response?.payload)) setLsaSetOptions(response.payload)
    }
    loadLsaSetOptions()
  }, [lsaSetEnabled])

  useEffect(() => {
    if (defaultsToggleEnabled) {
      const t = setTimeout(() => setDefaultsOverflowVisible(true), 320)
      return () => clearTimeout(t)
    }
    setDefaultsOverflowVisible(false)
  }, [defaultsToggleEnabled])

  useEffect(() => {
    const handleReqError = () => {
      reqError.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }

    handleReqError()
  }, [reqError])

  useEffect(() => {
    if (isEdit) {
      getGroupById()
      getGroupAffiliates()
      loadLegacyUsersIndex()
    }
  }, [])

  const validateProgramPlanDefaultsForm = () => {
    const block = groupInfo?.customSettings?.programPlanDefaults
    if (!block || block.enabled !== true) return null
    const tiers = Array.isArray(block.tiers) ? block.tiers.filter(t => !t?._removing) : []
    if (tiers.length === 0) {
      return 'Add at least one tier (the fallback row is enough on its own).'
    }
    const fallbackIndexes = tiers.map((t, i) => (t?.maxAmount === null || t?.maxAmount === '' || t?.maxAmount === undefined ? i : -1)).filter(i => i !== -1)
    if (fallbackIndexes.length === 0) {
      return 'Exactly one tier must be the fallback (the "Default (else)" row).'
    }
    if (fallbackIndexes.length > 1) {
      return 'Only one tier may be the fallback (the "Default (else)" row).'
    }
    if (fallbackIndexes[0] !== tiers.length - 1) {
      return 'The fallback row must be the last tier.'
    }
    let previousMax = null
    for (let i = 0; i < tiers.length - 1; i += 1) {
      const tier = tiers[i]
      const maxAmount = Number(tier?.maxAmount)
      if (!Number.isFinite(maxAmount) || maxAmount <= 0) {
        return `Tier ${i + 1}: Max Amount must be a positive number.`
      }
      if (previousMax !== null && maxAmount <= previousMax) {
        return 'Tier thresholds must be strictly increasing.'
      }
      previousMax = maxAmount
    }
    for (let i = 0; i < tiers.length; i += 1) {
      const tier = tiers[i]
      if (tier?.programFee != null && (Number.isNaN(tier.programFee) || tier.programFee < 0 || tier.programFee > 50)) {
        return `Tier ${i + 1}: Program Fee must be between 0 and 50.`
      }
      if (tier?.custodialFee != null && ![0, 50, 100].includes(Number(tier.custodialFee))) {
        return `Tier ${i + 1}: Custodial Fee must be one of $0, $50, or $100.`
      }
      if (tier?.programLength != null && (!Number.isInteger(Number(tier.programLength)) || Number(tier.programLength) < 1 || Number(tier.programLength) > 240)) {
        return `Tier ${i + 1}: Program Length must be a positive integer between 1 and 240.`
      }
    }
    return null
  }

  const normalizeTiersForSave = (tiers) => {
    if (!Array.isArray(tiers)) return []
    const cleaned = tiers
      .filter(t => !t?._removing)
      .map(t => ({
        maxAmount: t?.maxAmount === '' || t?.maxAmount === null || t?.maxAmount === undefined ? null : Number(t.maxAmount),
        programFee: t?.programFee === '' || t?.programFee === null || t?.programFee === undefined ? null : Number(t.programFee),
        custodialFee: t?.custodialFee === '' || t?.custodialFee === null || t?.custodialFee === undefined ? null : Number(t.custodialFee),
        programLength: t?.programLength === '' || t?.programLength === null || t?.programLength === undefined ? null : Number(t.programLength),
      }))
    return cleaned.sort((a, b) => {
      if (a.maxAmount === null) return 1
      if (b.maxAmount === null) return -1
      return a.maxAmount - b.maxAmount
    })
  }

  const updateTier = (index, patch) => {
    setGroupInfo((prev) => {
      const tiers = [...(prev?.customSettings?.programPlanDefaults?.tiers || [])]
      tiers[index] = { ...tiers[index], ...patch }
      return {
        ...prev,
        customSettings: {
          ...(prev?.customSettings || {}),
          programPlanDefaults: {
            ...(prev?.customSettings?.programPlanDefaults || {}),
            tiers,
          },
        },
      }
    })
  }

  const addTier = () => {
    setGroupInfo((prev) => {
      const existing = prev?.customSettings?.programPlanDefaults?.tiers || []
      const withoutFallback = existing.filter(t => t?.maxAmount !== null)
      const fallback = existing.find(t => t?.maxAmount === null) || makeEmptyTier(true)
      const tiers = [...withoutFallback, makeEmptyTier(false), fallback]
      return {
        ...prev,
        customSettings: {
          ...(prev?.customSettings || {}),
          programPlanDefaults: {
            ...(prev?.customSettings?.programPlanDefaults || {}),
            tiers,
          },
        },
      }
    })
  }

  const requestRemoveTier = (uid) => {
    setGroupInfo((prev) => {
      const tiers = (prev?.customSettings?.programPlanDefaults?.tiers || []).map(t =>
        t?._uid === uid ? { ...t, _removing: true } : t
      )
      return {
        ...prev,
        customSettings: {
          ...(prev?.customSettings || {}),
          programPlanDefaults: {
            ...(prev?.customSettings?.programPlanDefaults || {}),
            tiers,
          },
        },
      }
    })
  }

  const finalizeRemoveTier = (uid) => {
    setGroupInfo((prev) => {
      const tiers = (prev?.customSettings?.programPlanDefaults?.tiers || []).filter(t => t?._uid !== uid)
      return {
        ...prev,
        customSettings: {
          ...(prev?.customSettings || {}),
          programPlanDefaults: {
            ...(prev?.customSettings?.programPlanDefaults || {}),
            tiers,
          },
        },
      }
    })
  }

  const saveNewGroup = async () => {
    if (emailError) {
      showSnackBar("error", "Please fix email validation errors before saving")
      return
    }

    const defaultsError = validateProgramPlanDefaultsForm()
    if (defaultsError) {
      showSnackBar("error", defaultsError)
      return
    }

    triggerLoading(true)
    try {
      const dataToSave = {
        ...groupInfo,
        description,
        customSettings: {
          ...(groupInfo?.customSettings || {}),
          programPlanDefaults: {
            ...(groupInfo?.customSettings?.programPlanDefaults || {}),
            tiers: normalizeTiersForSave(groupInfo?.customSettings?.programPlanDefaults?.tiers),
          },
        },
      }
      const response = await dispatch(createNewContactGroup(dataToSave))
      if (response?.payload?.data?._id) {
        showSnackBar("success", "Contact group successfully created")
        navigate('/settings')
      } else if (response?.payload?.data) {
        const errorMessage = response?.payload?.data || 'An error occurred. Please try again.'
        setReqError(errorMessage)
      } else {
        setReqError('Failed to Add Contact Group')
      }
    } catch (error) {
      setReqError(error.message || 'Failed to Add Contact Group')
    }
    triggerLoading(false)
  }

  const editGroup = async () => {
    if (emailError) {
      showSnackBar("error", "Please fix email validation errors before saving")
      return
    }

    const defaultsError = validateProgramPlanDefaultsForm()
    if (defaultsError) {
      showSnackBar("error", defaultsError)
      return
    }

    triggerLoading(true)
    try {
      const dataToSave = {
        ...groupInfo,
        description,
        owners,
        supervisors,
        customSettings: {
          ...(groupInfo?.customSettings || {}),
          programPlanDefaults: {
            ...(groupInfo?.customSettings?.programPlanDefaults || {}),
            tiers: normalizeTiersForSave(groupInfo?.customSettings?.programPlanDefaults?.tiers),
          },
        },
      }
      const response = await dispatch(updateContactGroup({ groupId, userData: dataToSave }))
      if (response?.payload?.data?._id) {
        showSnackBar("success", "Contact group successfully updated")
        navigate('/settings')
      } else if (response?.payload?.data) {
        const errorMessage = response?.payload?.data || 'An error occurred. Please try again.'
        setReqError(errorMessage)
      } else {
        setReqError('Failed to Edit Contact Group')
      }
    } catch (error) {
      setReqError(error.message || 'Failed to Edit Contact Group')
    }
    triggerLoading(false)
  }

  const getGroupById = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getContactGroupById({ groupId }))
      const group = response?.payload?.data
      if (group?._id) {
        const incomingTiers = Array.isArray(group?.customSettings?.programPlanDefaults?.tiers)
          ? group.customSettings.programPlanDefaults.tiers
          : []
        const withUids = incomingTiers.map(t => ({ ...t, _uid: nextUid() }))
        const tiers = withUids.length > 0 ? withUids : initialTiers()
        const { programFee: _programFee, ...storedSettings } = group?.customSettings || {}
        const merged = {
          ...group,
          customSettings: {
            ...storedSettings,
            programPlanDefaults: {
              enabled: !!group?.customSettings?.programPlanDefaults?.enabled,
              tiers,
            },
          },
        }
        setGroupInfo(merged)
        setOwners(group?.owners)
        setSupervisors(group?.supervisors)
        setDescription(group?.description)
      } else if (group) {
        const errorMessage = response?.payload?.data || 'An error occurred. Please try again.'
        setReqError(errorMessage)
      } else {
        setReqError('Failed to Get Contact Group')
      }
    } catch (error) {
      setReqError(error.message || 'Failed to Get Contact Group')
    }
    triggerLoading(false)
  }

  const loadLegacyUsersIndex = async () => {
    try {
      const response = await dispatch(fetchAllAffiliatesDropdown({ affiliatesOnly: false }))
      if (response?.payload?.data) {
        setLegacyUsersIndex(response.payload.data)
      }
    } catch (error) {
      setReqError(error.message || 'Failed to resolve owner names')
    }
  }

  const getGroupAffiliates = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getContactGroupAffiliates(groupId))
      if (response?.payload?.data) {
        setGroupAffiliates(response?.payload?.data)
      } else if (response?.payload?.data) {
        const errorMessage = response?.payload?.data || 'An error occurred. Please try again.'
        setReqError(errorMessage)
      } else {
        setReqError('Failed to Get Contact Group Affiliates')
      }
    } catch (error) {
      setReqError(error.message || 'Failed to Get Contact Group Affiliates')
    }
    triggerLoading(false)
  }

  const validateEmail = (email) => {
    if (!email || email.trim().length === 0) {
      return true
    }
    const emailRegex = /^\S+@\S+\.\S+$/
    return emailRegex.test(email)
  }

  const handleInputChange = ({ name, value }) => {
    if (name === 'qcCallDaysBeforeWelcome') {
      const numValue = parseInt(value, 10)
      if (!isNaN(numValue) && numValue >= 3 && numValue <= 5) {
        setGroupInfo((prev) => ({ ...prev, [name]: numValue }))
      }
      return
    }

    setGroupInfo((prev) => ({ ...prev, [name]: value }))

    if (name === 'notificationEmail') {
      setEmailTouched(true)
      if (value && !validateEmail(value)) {
        setEmailError('Please enter a valid email address')
      } else {
        setEmailError('')
      }
    }
  }

  const getAssigneesData = ({ assignee }) => {
    const inGroup = groupAffiliates?.find(el => el.value === assignee)
    if (inGroup) {
      return inGroup.label
    }
    const legacy = legacyUsersIndex?.find(el => el.value === assignee)
    if (legacy) {
      return legacy.label
    }
    const fromGroupInfo = groupInfo?.owners?.find(el => el?._id === assignee || el === assignee)
    if (fromGroupInfo?.firstName || fromGroupInfo?.lastName) {
      return `${fromGroupInfo?.firstName || ''} ${fromGroupInfo?.lastName || ''}`.trim()
    }
    return assignee
  }

  return (
    <ContainerPage>
      <div className="flex px-6 w-[79%] flex-col gap-6 mb-[25px]">
        <div>
          <h1 className="font-bold text-xl mb-2 w-full">
            {isEdit ? "Edit Contact Group" : "Add Contact Group"}
          </h1>
          <Breadcrumbs title={BreadcrumbsTitle} titleActive={BreadcrumbsTitleActive}
            onBreadcrumbClick={handleBreadcrumbClick} />
        </div>
        <div className="mx-[95px] mt-10 px-6 py-5 rounded-2xl shadow-lg bg-surface border border-divider">
          <div className="flex flex-col gap-6">
            <Input
              placeholder="Enter Contact Group Name"
              label="Contact Group Name *"
              value={groupInfo?.name}
              onChange={(e) => handleInputChange({ name: 'name', value: e.target.value })}
            />
            <TextArea text={description}
              setText={setDescription}
              placeholder="Please input the description"
              title={"Description *"} onChange={(e) => handleInputChange({ name: 'description', value: e })} />
            <div>
              <Input
                placeholder="email@example.com"
                label="Notification Email"
                type="email"
                value={groupInfo?.notificationEmail || ''}
                onChange={(e) => handleInputChange({ name: 'notificationEmail', value: e.target.value })}
                error={emailTouched && !!emailError}
              />
              {isEdit && <OfficeEmailMuteHint groupId={groupId} address={groupInfo?.notificationEmail} />}
              {emailTouched && emailError && (
                <p className="text-red-500 text-sm mt-1">{emailError}</p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="isDepartment"
                checked={groupInfo?.isDepartment || false}
                onChange={() => handleInputChange({ name: 'isDepartment', value: !groupInfo?.isDepartment })}
              />
              <label htmlFor="isDepartment" className="text-sm font-medium cursor-pointer select-none">
                Department (internal organizational unit for staff users)
              </label>
            </div>

            <div className="border-t border-gray-200 pt-6 mt-2">
              <h3 className="text-lg font-semibold mb-4">Quality Control Call Settings</h3>

              <div className="flex items-center gap-3 mb-4">
                <input
                  type="checkbox"
                  id="qcCallEnabled"
                  checked={groupInfo?.qcCallEnabled}
                  onChange={(e) => handleInputChange({ name: 'qcCallEnabled', value: e.target.checked })}
                  className="w-5 h-5 cursor-pointer"
                />
                <label htmlFor="qcCallEnabled" className="text-sm font-medium cursor-pointer">
                  Enable Quality Control Call for this Contact Group
                </label>
              </div>
            </div>

            <div className={`border-t border-gray-200 pt-6 mt-2 ${isEdit ? 'border-b pb-6' : ''}`}>
              <h3 className="text-lg font-semibold mb-4">Custom values for this group</h3>

              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex rounded-lg border border-divider overflow-hidden">
                  {[
                    { value: 'english', label: 'English' },
                    { value: 'bilingual', label: 'Bilingual' },
                  ].map((option) => {
                    const isActive = (groupInfo?.customSettings?.lsaTemplateType || 'english') === option.value
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`px-4 py-1.5 text-sm font-medium transition-colors ${isActive ? 'bg-primary-main text-primary-contrast' : 'bg-transparent text-text-secondary hover:text-text-primary'}`}
                        onClick={() => setGroupInfo((prev) => ({
                          ...prev,
                          customSettings: {
                            ...prev.customSettings,
                            lsaTemplateType: option.value,
                          },
                        }))}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
                <span className="text-sm font-medium select-none text-text-primary">Default LSA Template</span>
              </div>

              {!groupInfo?.isDepartment && (
                <div className="flex items-center gap-3 mb-4">
                  <div className="inline-flex rounded-lg border border-divider overflow-hidden">
                    {[
                      { value: 'FULL', label: 'Full' },
                      { value: 'RATIOS_ONLY', label: 'Ratios only' },
                      { value: 'HIDDEN', label: 'Hidden' },
                    ].map((option) => {
                      const isActive = (groupInfo?.customSettings?.dashboardVisibility || 'FULL') === option.value
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={`px-4 py-1.5 text-sm font-medium transition-colors ${isActive ? 'bg-primary-main text-primary-contrast' : 'bg-transparent text-text-secondary hover:text-text-primary'}`}
                          onClick={() => setGroupInfo((prev) => ({
                            ...prev,
                            customSettings: {
                              ...prev.customSettings,
                              dashboardVisibility: option.value,
                            },
                          }))}
                        >
                          {option.label}
                        </button>
                      )
                    })}
                  </div>
                  <span className="text-sm font-medium select-none text-text-primary">Dashboard visibility for this group (owners always keep full access)</span>
                </div>
              )}

              {/* A permission, not a price and not a mode. It ADDS the flat option at enrolment;
                  the percentage stays available and stays the default. The amount is the rep's,
                  chosen per client, because two clients here are not owed the same number. */}
              <div className="flex flex-col gap-2 mb-4">
                <div className="flex items-center gap-3">
                  <Switch
                    id="flatFeeAllowed"
                    checked={isFlatFeeAllowed(groupInfo?.customSettings)}
                    onChange={(next) => setGroupInfo((prev) => ({
                      ...prev,
                      customSettings: {
                        ...prev.customSettings,
                        flatFeeAllowed: next,
                        ...(flatFeeOnlyEnabled && !next ? { flatFeeOnly: false } : {}),
                      },
                    }))}
                  />
                  <label htmlFor="flatFeeAllowed" className="text-sm font-medium cursor-pointer select-none text-text-primary">
                    Allow a flat program fee for this office
                  </label>
                </div>

                <span className="text-sm select-none text-text-secondary">
                  {isFlatFeeAllowed(groupInfo?.customSettings)
                    ? 'Reps here can charge one agreed amount instead of a percentage, and choose the amount per client. The percentage is still available and is still what they get by default.'
                    : 'The fee is a percentage of enrolled debt, set per client on the program plan.'}
                </span>
              </div>

              {/* WS #2219702 (PACK, Hayden). Only where a flat fee is allowed: on its own it would
                  leave the rep no fee type at all. */}
              {flatFeeOnlyEnabled && isFlatFeeAllowed(groupInfo?.customSettings) && (
                <div className="flex flex-col gap-2 mb-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="flatFeeOnly"
                      checked={isFlatFeeOnlyOffice(groupInfo?.customSettings)}
                      onChange={(next) => setGroupInfo((prev) => ({
                        ...prev,
                        customSettings: { ...prev.customSettings, flatFeeOnly: next },
                      }))}
                    />
                    <label htmlFor="flatFeeOnly" className="text-sm font-medium cursor-pointer select-none text-text-primary">
                      Reps here enrol on a flat fee only
                    </label>
                  </div>

                  <span className="text-sm select-none text-text-secondary">
                    {isFlatFeeOnlyOffice(groupInfo?.customSettings)
                      ? 'Reps here see no percentage option: every new client starts on a flat fee, one payment. A percentage is refused.'
                      : 'Reps here choose a percentage or a flat fee for each client.'}
                  </span>
                </div>
              )}

              {/* WS #2219702. Independent of the fee switches. Only the payment schedule table goes;
                  the List of Creditors and the authorization pages stay. */}
              {flatFeeOnlyEnabled && (
                <div className="flex flex-col gap-2 mb-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="hideLsaPaymentSchedule"
                      checked={hidesLsaPaymentSchedule(groupInfo?.customSettings)}
                      onChange={(next) => setGroupInfo((prev) => ({
                        ...prev,
                        customSettings: { ...prev.customSettings, hideLsaPaymentSchedule: next },
                      }))}
                    />
                    <label htmlFor="hideLsaPaymentSchedule" className="text-sm font-medium cursor-pointer select-none text-text-primary">
                      Leave the payment schedule out of the LSA
                    </label>
                  </div>

                  <span className="text-sm select-none text-text-secondary">
                    {hidesLsaPaymentSchedule(groupInfo?.customSettings)
                      ? 'The agreement for clients here has no payment schedule table. The list of creditors and the payment authorizations stay.'
                      : 'The agreement for clients here includes the payment schedule table.'}
                  </span>
                </div>
              )}

              {lsaSetEnabled && offeredLsaSets.length > 0 && (
                <div className="flex items-center gap-3 mb-4">
                  <div
                    role="group"
                    aria-labelledby="lsaSetLabel"
                    className="inline-flex rounded-lg border border-divider overflow-hidden"
                  >
                    {offeredLsaSets.map((choice) => {
                      const isActive = currentLsaSet === choice.value
                      return (
                        <button
                          key={choice.value}
                          type="button"
                          aria-pressed={isActive}
                          className={`px-4 py-1.5 text-sm font-medium transition-colors ${isActive ? 'bg-primary-main text-primary-contrast' : 'bg-transparent text-text-secondary hover:text-text-primary'}`}
                          onClick={() => setGroupInfo((prev) => ({
                            ...prev,
                            customSettings: {
                              ...prev.customSettings,
                              lsaSet: choice.value,
                            },
                          }))}
                        >
                          {choice.label}
                        </button>
                      )
                    })}
                  </div>
                  <span id="lsaSetLabel" className="text-sm font-medium select-none text-text-primary">LSA set</span>
                </div>
              )}

              {/* Who collects the money. Turning this on is what lets a rep finish an enrolment
                  with no banking details at all; the client still gets a real plan and real
                  payments, they just wait for a human to mark each one paid. */}
              <div className="flex flex-col gap-2 mb-4">
                <div className="flex items-center gap-3">
                  <Switch
                    id="affiliateBilled"
                    checked={isAffiliateBilledOffice(groupInfo?.customSettings)}
                    onChange={(next) => setGroupInfo((prev) => ({
                      ...prev,
                      customSettings: { ...prev.customSettings, affiliateBilled: next },
                    }))}
                  />
                  <label htmlFor="affiliateBilled" className="text-sm font-medium cursor-pointer select-none text-text-primary">
                    The affiliate collects payments for this office
                  </label>
                </div>

                <span className="text-sm select-none text-text-secondary">
                  {isAffiliateBilledOffice(groupInfo?.customSettings)
                    ? affiliateBilledOnText
                    : 'Clients here pay us directly, with a bank account or a card.'}
                </span>
              </div>

              {/* WS #2453778. Only for an office the affiliate collects for. Absent reads as ON, so an
                  office nobody set (PACK) keeps its cards guarded; only a real OFF lets us charge them. */}
              {affiliateCollectsCardsEnabled && isAffiliateBilledOffice(groupInfo?.customSettings) && (
                <div className="flex flex-col gap-2 mb-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="affiliateCollectsCards"
                      checked={affiliateCollectsCards(groupInfo?.customSettings)}
                      onChange={(next) => setGroupInfo((prev) => ({
                        ...prev,
                        customSettings: { ...prev.customSettings, affiliateCollectsCards: next },
                      }))}
                    />
                    <label htmlFor="affiliateCollectsCards" className="text-sm font-medium cursor-pointer select-none text-text-primary">
                      The affiliate also collects card payments
                    </label>
                  </div>

                  <span className="text-sm select-none text-text-secondary">
                    {affiliateCollectsCards(groupInfo?.customSettings)
                      ? AFFILIATE_COLLECTS_CARDS_ON_TEXT
                      : AFFILIATE_COLLECTS_CARDS_OFF_TEXT}
                  </span>
                </div>
              )}

              {paymentMethodsEnabled && (
                <div className="flex items-center gap-3 mb-4">
                  <div
                    role="group"
                    aria-labelledby="acceptedPaymentMethodsLabel"
                    className="inline-flex rounded-lg border border-divider overflow-hidden"
                  >
                    {PAYMENT_METHOD_OPTIONS.map((option) => {
                      const isActive = acceptedMethodsValue === option.value
                      return (
                        <button
                          key={option.value}
                          type="button"
                          aria-pressed={isActive}
                          className={`px-4 py-1.5 text-sm font-medium transition-colors ${isActive ? 'bg-primary-main text-primary-contrast' : 'bg-transparent text-text-secondary hover:text-text-primary'}`}
                          onClick={() => setGroupInfo((prev) => ({
                            ...prev,
                            customSettings: {
                              ...prev.customSettings,
                              acceptedPaymentMethods: [...option.methods],
                            },
                          }))}
                        >
                          {option.label}
                        </button>
                      )
                    })}
                  </div>
                  <span id="acceptedPaymentMethodsLabel" className="text-sm font-medium select-none text-text-primary">Accepted payment methods</span>
                </div>
              )}

              {/* WS #2296193: the only Lead Sources a lead of this office may take; the server trims and drops blank lines. */}
              {officeLeadSourcesEnabled && (
                <div className="flex flex-col gap-2 mb-4">
                  <TextArea
                    title="Lead Source options for this office"
                    placeholder="One Lead Source per line"
                    text={(groupInfo?.customSettings?.leadSources || []).join('\n')}
                    setText={(text) => setGroupInfo((prev) => ({
                      ...prev,
                      customSettings: { ...prev.customSettings, leadSources: text ? text.split('\n') : [] },
                    }))}
                  />
                  <span className="text-sm select-none text-text-secondary">
                    {(groupInfo?.customSettings?.leadSources || []).some((line) => line.trim())
                      ? 'Reps of this office pick Lead Source from this list only.'
                      : 'Empty: reps of this office pick or type any Lead Source, as today.'}
                  </span>
                </div>
              )}

              {reversalStatusesEnabled && (
                <div className="flex flex-col gap-2 mb-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="reversalStatusesAllowed"
                      checked={isReversalOffice(groupInfo?.customSettings)}
                      onChange={(next) => setGroupInfo((prev) => ({
                        ...prev,
                        customSettings: { ...prev.customSettings, reversalStatusesAllowed: next },
                      }))}
                    />
                    <label htmlFor="reversalStatusesAllowed" className="text-sm font-medium cursor-pointer select-none text-text-primary">
                      Reps here can mark a client Chargeback or Refunded
                    </label>
                  </div>

                  <span className="text-sm select-none text-text-secondary">
                    {isReversalOffice(groupInfo?.customSettings)
                      ? "Reps of this office can flag a client as Chargeback or Refunded on the client's file. The file stays open."
                      : 'Only MDL staff change client statuses for this office.'}
                  </span>
                </div>
              )}

              {callRecordingsEnabled && (
                <div className="flex flex-col gap-2 mb-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="callRecordingsAccess"
                      checked={isCallRecordingsOffice(groupInfo?.customSettings)}
                      onChange={(next) => setGroupInfo((prev) => ({
                        ...prev,
                        customSettings: { ...prev.customSettings, callRecordingsAccess: next },
                      }))}
                    />
                    <label htmlFor="callRecordingsAccess" className="text-sm font-medium cursor-pointer select-none text-text-primary">
                      Call recordings (MP3) and document access for reps
                    </label>
                  </div>

                  <span className="text-sm select-none text-text-secondary">
                    {isCallRecordingsOffice(groupInfo?.customSettings)
                      ? "Staff and this office's reps can upload MP3 call recordings on this office's clients, and this office's reps can open and download every document on those clients."
                      : "No MP3 call recordings for this office's clients. Reps of this office cannot open or download documents."}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <Switch
                  id="disableInitialFee"
                  checked={groupInfo?.customSettings?.disableInitialFee || false}
                  onChange={() => {
                    setGroupInfo((prev) => ({
                      ...prev,
                      customSettings: {
                        ...prev.customSettings,
                        disableInitialFee: !prev.customSettings?.disableInitialFee,
                      },
                    }))
                  }}
                />
                <label htmlFor="disableInitialFee" className="text-sm font-medium cursor-pointer select-none">
                  Disable Enrollment Fee (affiliates cannot set an enrollment fee, locked at $0)
                </label>
              </div>

              <div className="flex flex-col gap-2 mb-4">
                <div className="flex items-center gap-3">
                  <Switch
                    id="programPlanDefaultsEnabled"
                    checked={!!groupInfo?.customSettings?.programPlanDefaults?.enabled}
                    onChange={(checked) => setGroupInfo((prev) => ({
                      ...prev,
                      customSettings: {
                        ...(prev?.customSettings || {}),
                        programPlanDefaults: {
                          ...(prev?.customSettings?.programPlanDefaults || {}),
                          enabled: checked,
                        },
                      },
                    }))}
                  />
                  <label htmlFor="programPlanDefaultsEnabled" className="text-sm font-medium cursor-pointer select-none">
                    Pre-fill new plans from debt-size tiers
                  </label>
                </div>

                <span className="text-sm select-none text-text-secondary">
                  Only the tier table below. The settings above apply either way.
                </span>
              </div>

              <div
                className={`transition-all duration-300 ease-in-out ${
                  defaultsOverflowVisible ? 'overflow-visible' : 'overflow-hidden'
                } ${
                  defaultsToggleEnabled
                    ? 'max-h-[60rem] opacity-100 mt-2'
                    : 'max-h-0 opacity-0 mt-0'
                }`}
              >
                <div className="text-sm text-text-secondary mb-2 flex items-center gap-2">
                  <span>Auto-apply values based on total debts. The "Default (else)" row applies when no threshold matches.</span>
                  <HintIcon>
                    Program Fee 0–50% (closest valid value applied per role). Custodial Fee $0/$50/$100. Program Length 1–240 payments.
                  </HintIcon>
                </div>
                <div className="text-sm">
                  <div className="grid grid-cols-[1fr_1fr_1fr_1fr_40px] gap-3 text-text-secondary font-medium pb-2 border-b border-divider">
                    <div>Max Amount (≤)</div>
                    <div>Program Fee %</div>
                    <div>Custodial Fee</div>
                    <div>Program Length (payments)</div>
                    <div></div>
                  </div>
                  {(groupInfo?.customSettings?.programPlanDefaults?.tiers || []).map((tier, index, arr) => {
                    const isFallback = tier?.maxAmount === null || tier?.maxAmount === undefined
                    const isLast = index === arr.length - 1
                    return (
                      <Collapse
                        key={tier._uid || index}
                        in={!tier?._removing}
                        appear
                        timeout={260}
                        onExited={() => finalizeRemoveTier(tier._uid)}
                        unmountOnExit
                      >
                        <div
                          className={`grid grid-cols-[1fr_1fr_1fr_1fr_40px] gap-3 items-start py-2 border-b border-divider transition-opacity duration-200 ${tier?._removing ? 'opacity-0' : 'opacity-100'}`}
                        >
                          <div>
                            {isFallback && isLast ? (
                              <div className="h-[42px] flex items-center text-text-secondary italic">Default (else)</div>
                            ) : (
                              <Input
                                placeholder="e.g. 3000"
                                type="number"
                                withLabel={false}
                                value={tier?.maxAmount ?? ''}
                                onChange={(e) => {
                                  const raw = e.target.value
                                  updateTier(index, { maxAmount: raw === '' ? '' : Number(raw) })
                                }}
                              />
                            )}
                          </div>
                          <div>
                            <Input
                              placeholder="e.g. 40"
                              type="number"
                              withLabel={false}
                              value={tier?.programFee ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value
                                updateTier(index, { programFee: raw === '' ? null : Number(raw) })
                              }}
                            />
                          </div>
                          <div>
                            <Dropdown
                              placeholder="$"
                              variant="standard"
                              withLabel={false}
                              value={tier?.custodialFee ?? 50}
                              menuItems={[
                                { label: '$0', value: 0 },
                                { label: '$50', value: 50 },
                                { label: '$100', value: 100 },
                              ]}
                              setValue={(val) => updateTier(index, { custodialFee: val })}
                            />
                          </div>
                          <div>
                            <Input
                              placeholder="e.g. 24"
                              type="number"
                              withLabel={false}
                              value={tier?.programLength ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value
                                updateTier(index, { programLength: raw === '' ? null : Number(raw) })
                              }}
                            />
                          </div>
                          <div className="flex items-center h-[42px]">
                            {!(isFallback && isLast) && (
                              <button
                                type="button"
                                className="p-1 rounded hover:bg-surface text-text-secondary hover:text-text-primary transition-colors"
                                onClick={() => requestRemoveTier(tier._uid)}
                                aria-label={`Remove tier ${index + 1}`}
                                disabled={tier?._removing}
                              >
                                <CrossCloseIcon fill="var(--color-gray-600)" />
                              </button>
                            )}
                          </div>
                        </div>
                      </Collapse>
                    )
                  })}
                </div>
                <div className="mt-3">
                  <Button type="outlined" size="sm" width="w-[120px]" onClick={addTier}>+ Add tier</Button>
                </div>
              </div>
            </div>

            {isEdit && (
              <div>
                {<Dropdown
                  value={owners}
                  setValue={(e) => setOwners(prev => [...prev, e])}
                  menuItems={groupAffiliates?.filter(affiliate => !owners?.includes(affiliate?.value))}
                  className=""
                  variant="standard"
                  placeholder="John Doe"
                  label="Owners"
                />}
                <div className="w-[350px] flex flex-wrap text-md mt-4  basis-1/2 ">
                  {owners?.map((assignee, index) => (
                    <div key={index}
                      className="flex flex-row items-center justify-between whitespace-nowrap mr-4 mb-2">
                      <div>{getAssigneesData({ assignee })}</div>
                      <button className="ml-2" onClick={() => setOwners(prev => prev.filter(el => el !== assignee))}>
                        <CrossCloseIcon fill="var(--color-gray-600)" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {isEdit && (
              <div>
                {<Dropdown
                  value={supervisors}
                  setValue={(e) => setSupervisors(prev => [...prev, e])}
                  menuItems={groupAffiliates?.filter(affiliate => !supervisors?.includes(affiliate?.value))}
                  className=""
                  variant="standard"
                  placeholder="John Doe"
                  label="Overseer"
                />}
                <div className="w-[350px] flex flex-wrap text-md mt-4  basis-1/2 ">
                  {supervisors?.map((assignee, index) => (
                    <div key={index}
                      className="flex flex-row items-center justify-between whitespace-nowrap mr-4 mb-2">
                      <div>{getAssigneesData({ assignee })}</div>
                      <button className="ml-2" onClick={() => setSupervisors(prev => prev.filter(el => el !== assignee))}>
                        <CrossCloseIcon fill="var(--color-gray-600)" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-end gap-4">
            </div>
          </div>
          <div className="flex justify-end items-center gap-4 self-stretch py-6 border-t border-divider">
            <Button onClick={() => navigate("/settings", {
              replace: true,
              state: { isCreate: true }
            })} type="outlined" size="lg" width="w-[156px]">Cancel</Button>
            <Button size="lg" width="w-[156px]" onClick={isEdit ? editGroup : saveNewGroup}>Save</Button>
          </div>
        </div>
      </div>
    </ContainerPage>
  )
}
export default ContactGroupControl